import {
  Typography,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Button,
  Box,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import * as yup from "yup";
import { I18n } from "react-redux-i18n";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUnmount } from "react-use";
import { useTheme } from "@material-ui/styles";
import FormButton from "./FormButton";
import { Auth } from "aws-amplify";
import { AUTHENTICATOR_SIGNIN, AUTHENTICATOR_VERIFY } from "./constants";

const Signup = ({ environment, initialValue, setInitialValue, setAuthScreen }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(false);

  const schema = useMemo(() => {
    return yup.object({
      email: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .email(I18n.t("authenticator.emailRequired")),
      password: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .min(6, I18n.t("authenticator.minLength")),
      confirmPassword: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .test(
          "passwords-match",
          I18n.t("authenticator.passwordsNotMatch"),
          function (value) {
            return this.parent.password === value;
          }
        ),
      agreeTerms: yup
        .boolean()
        .oneOf([true], I18n.t("authenticator.tncRequired")),
    });
  }, []);

  const {
    reset,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      email: initialValue.email,
      password: initialValue.password,
      confirmPassword: initialValue.confirmPassword,
      agreeTerms: initialValue.agreeTerms,
    },
    resolver: yupResolver(schema),
  });

  const emailErrorMsg = useMemo(() => {
    if (error) return I18n.t("authenticator.UsernameExistsException");

    return errors?.email?.message || "";
  }, [errors?.email?.message, error]);

  useEffect(() => {
    reset({
      email: initialValue.email,
      password: initialValue.password,
      confirmPassword: initialValue.confirmPassword,
      agreeTerms: initialValue.agreeTerms,
    });
  }, [
    initialValue.email,
    initialValue.password,
    initialValue.confirmPassword,
    initialValue.agreeTerms,
    reset,
  ]);

  useUnmount(() => {
    setInitialValue({ ...initialValue, ...getValues() });
  });

  const onNewsletterChange = (category, value, checked) => {
    setInitialValue({
      ...initialValue,
      newsletters: initialValue.newsletters.map((nl) => {
        if (nl.category === category)
          return {
            category,
            status: checked ? "subscribed" : "unsubscribed",
            value,
          };

        return nl;
      }),
    });
  };

  const onSubmit = async (data) => {
    setError(false);
    setLoader(true);

    try {
      await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes: {
          email: data.email,
        },
      });

      return setAuthScreen(AUTHENTICATOR_VERIFY);
    } catch (e) {
      console.log(e);

      if (e.code === "UsernameExistsException") setError(true);
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
          }}
        >
          {I18n.t("authenticator.signUp")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.email")}
              variant="outlined"
              {...field}
              error={(isSubmitted && Boolean(errors.email)) || error}
              helperText={emailErrorMsg}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.password")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.password)}
              helperText={errors?.password?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.repeatPass")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.confirmPassword)}
              helperText={errors?.confirmPassword?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      {initialValue?.newsletters?.map(({ category, value }, index) => {
        return (
          <Grid
            item
            xs={12}
            style={{ display: "flex", flexDirection: "column" }}
            key={`newsletter-${category}-${index}`}
          >
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={
                      initialValue.newsletters[index].status === "subscribed"
                    }
                    onChange={(e) => {
                      onNewsletterChange(category, value, e.target.checked);
                    }}
                  />
                }
                label={
                  <Typography color="textSecondary">
                    {I18n.t("authenticator")[value]}
                  </Typography>
                }
              />
            </FormControl>
          </Grid>
        );
      })}

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="agreeTerms"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControl error={Boolean(errors?.agreeTerms?.message)}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    {...field}
                    style={{
                      color:
                        errors?.agreeTerms?.message && theme.palette.error.main,
                    }}
                  />
                }
                label={
                  <Typography
                    color={
                      errors?.agreeTerms?.message ? "error" : "textSecondary"
                    }
                  >
                    {I18n.t("authenticator.agreeTo")}{" "}
                    <a
                      href={I18n.t("authenticator.terms-and-conditions-url", environment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: errors?.agreeTerms?.message
                          ? theme.palette.error.main
                          : theme.palette.questionnaire.textPrimary,
                        fontWeight: 400,
                      }}
                    >
                      {I18n.t("authenticator.terms-and-conditions")}
                    </a>
                  </Typography>
                }
              />
              {errors?.agreeTerms?.message && (
                <FormHelperText style={{ marginTop: 0 }}>
                  {errors.agreeTerms.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton
          {...{ label: I18n.t("authenticator.createAccount"), loader }}
        />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography style={{ marginRight: "4px" }} color="textSecondary">
          {I18n.t("authenticator.alreadyMember")}
        </Typography>

        <Button
          type="submit"
          color="primary"
          onClick={() => setAuthScreen(AUTHENTICATOR_SIGNIN)}
        >
          {I18n.t("authenticator.signIn")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default Signup;
